import {apollo} from "@/apollo";
import gql from "graphql-tag";
import SchedulerEvent, {WriteSchedulerEvent} from "@/models/schedulerEvent";
import utils from "@/services/utils";
import Period from "@/models/period";
import {WritePatient} from "@/models/patient";

class SchedulerService {

    async generatePlanForWeek(date: string, officeId: number, regenerateExisting: boolean) {
        return await apollo.mutate({
            mutation: gql`mutation($date: LocalDate, $officeId: Int, $regenerateExisting: Boolean) {
                generatePlanForWeek(date: $date, officeId: $officeId, regenerateExisting: $regenerateExisting)
            }`,
            variables: {
                date: date,
                officeId: officeId,
                regenerateExisting: regenerateExisting
            }
        });
    }

    async savePlanForWeek(date: string, officeId: number) {
        return await apollo.mutate({
            mutation: gql`mutation($date: LocalDate, $officeId: Int) {
                savePlanForWeek(date: $date, officeId: $officeId)
            }`,
            variables: {
                date: date,
                officeId: officeId
            }
        });
    }

    subscribeForAutomaticPlanning(officeId: number) {
        return apollo.subscribe({
            query: gql`subscription($officeId: Int!) {
                subscribeForAutomaticPlanning(officeId: $officeId) {
                    Id
                    Subject
                    Description
                    Name
                    StartTime
                    EndTime
                    ProjectId
                    TaskId
                    IsBlock
                    PrimaryColor
                    SecondaryColor
                    Constraint
                }
            }`,
            variables: {officeId: officeId},
        });
    }

    async eventsForTherapist(therapistId: number) {
        const promise = await apollo.query({
            query: gql`
                query($therapistId: Int!) {
                    eventsForTherapist(therapistId: $therapistId) {
                        id
                        patient{
                            id
                            name
                            surname
                            type
                        }
                        therapist{
                            id
                            name
                            surname
                            type
                        }
                        activity{
                            id
                            code
                            name
                        }
                        description
                        status
                        paymentStatus
                        dateFrom
                        timeFrom
                        duration
                    }
                }
            `,
            variables: {therapistId: therapistId}
        });
        return (promise.data.eventsForTherapist as any[]).map<SchedulerEvent>(it => {
            return new SchedulerEvent(it)
        })
    }

    async eventsForPatient(patientId: number) {
        const promise = await apollo.query({
            query: gql`
                query($patientId: Int!) {
                    eventsForPatient(patientId: $patientId) {
                        id
                        patient{
                            id
                            name
                            surname
                            type
                        }
                        therapist{
                            id
                            name
                            surname
                            type
                        }
                        activity{
                            id
                            code
                            name
                        }
                        description
                        status
                        paymentStatus
                        dateFrom
                        timeFrom
                        duration
                    }
                }
            `,
            variables: {patientId: patientId}
        });
        return (promise.data.eventsForPatient as any[]).map<SchedulerEvent>(it => {
            return new SchedulerEvent(it)
        })
    }

    async addEvent(event: SchedulerEvent) {
        if (utils.isNull(event.therapist) || utils.isNull(event.patient) || utils.isNull(event.activity) || utils.isNull(event.dateFrom)) {
            return false;
        }
        const ev = new WriteSchedulerEvent(event.id, event.therapist.id!, event.patient.id!, event.activity.id!, event.description, event.status, event.paymentStatus   , event.dateFrom, event.timeFrom!!, event.duration!!);
        const promise = await apollo.mutate(
            {
                mutation: gql`mutation ($schedulerEvent: WriteSchedulerEvent!) {
                    writeSchedulerEvent(schedulerEvent: $schedulerEvent)
                }`, variables: {schedulerEvent: ev}
            }
        );
        return promise.data.writeSchedulerEvent;
    }

    async updateEvent(event: SchedulerEvent) {
        if (utils.isNull(event.id) || utils.isNull(event.dateFrom)) {
            return false;
        }
        const ev = new WriteSchedulerEvent(event.id, event.therapist.id!, event.patient.id!, event.activity.id!, event.description, event.status, event.paymentStatus, event.dateFrom, event.timeFrom!!, event.duration!!);
        const promise = await apollo.mutate(
            {
                mutation: gql`mutation ($schedulerEvent: WriteSchedulerEvent!) {
                    updateSchedulerEvent(schedulerEvent: $schedulerEvent)
                }`, variables: {schedulerEvent: ev}
            }
        );
        return promise.data.updateSchedulerEvent;
    }

    async removeEvent(eventId: number) {
        if (eventId && eventId <= 0) {
            return false;
        }
        const promise = await apollo.mutate({
            mutation: gql`mutation($eventId: Int!) {
                deleteSchedulerEvent(eventId: $eventId)
            }`,
            variables: {eventId: eventId}

        });
        return promise.data.deleteSchedulerEvent;
    }

    async getPeriods() {
        const promise = await apollo.query({
            query: gql`query {
                periods{
                    id
                    dateFrom
                    dateTo
                    patients {
                        id
                        name
                        surname
                        therapyUnits {
                            id
                            activity {
                                id
                                name
                            }
                            duration
                            weeklyNumber
                        }
                    }
                    availability
                    occupancy
                }
            }`
        });
        // eslint-disable-next-line no-console
        console.log(promise.data);
        return promise.data.periods;
    }

    async addPeriod(period: Period) {
        const promise = await apollo.mutate({
            mutation: gql`mutation ($period: WritePeriod) {
                addPeriod(period: $period)
            }`,
            variables: {period: {dateFrom: period.dateFrom, dateTo: period.dateTo}}
        });
        return promise.data.addPeriod;
    }

    async updatePeriod(period: Period) {
        const promise = await apollo.mutate({
            mutation: gql`mutation ($period: UpdatePeriod) {
                updatePeriod(period: $period)
            }`,
            variables: {period: {id: period.id, name: period.name, dateFrom: period.dateFrom, dateTo: period.dateTo}}
        });
        return promise.data.updatePeriod;
    }

    async deletePeriod(periodId: number) {
        const promise = await apollo.mutate({
            mutation: gql`mutation ($periodId: Int) {
                deletePeriod(id: $periodId)
            }`,
            variables: {periodId: periodId}
        });
        return promise.data.deletePeriod;
    }

    async removePlan(userId: number, userType: string, date: string) {
        const promise = await apollo.mutate({
            mutation: gql`mutation ($userId: Int, $userType: String, $date: LocalDate) {
                removePlan(userId: $userId, userType: $userType, date: $date)
            }`,
            variables: {userId: userId, userType: userType, date: date}
        });
        return promise.data.removePlan;
    }

    async savePatients(periodId: number, p: any[]) {
        const ids = p.map(it => Number(it.id))
        const promise = await apollo.mutate({
            mutation: gql`mutation ($periodId: Int!, $patientsIds: [Int]!){
                savePatients(periodId: $periodId, patientsIds: $patientsIds)
            }`,
            variables: {patientsIds: ids, periodId: periodId}
        });
        return promise.data.savePatients;
    }

    async loadSummary(partyId: number) {
        const promise = await apollo.query({
            query: gql` query ($partyId: Int!) {
                summary(partyId: $partyId){
                    month
                    activity
                    status
                    count
                }
            }`,
            variables: {partyId: partyId},
        });
        // eslint-disable-next-line no-console
        console.log(promise.data);
        return promise.data.summary;
    }

    async updatePatient(patient: WritePatient) {
        const promise = await apollo.mutate({
            mutation: gql` mutation ($patient: WritePatient!) {
                writePatient(patient: $patient)
            }`,
            variables: {patient: patient},
        });
        // eslint-disable-next-line no-console
        console.log(promise.data);
        return promise.data.updatePatient;
    }

    async patientsForPeriod() {
        const promise = await apollo.query({
            query: gql` query {
                patientsForPeriod{
                    id
                    name
                    surname
                }
            }`
        });
        return promise.data.patientsForPeriod;
    }

    async period(periodId: number) {
        const promise = await apollo.query({
            query: gql`query ($periodId: Int!){
                period(periodId: $periodId) {
                    id
                    name
                    dateFrom
                    dateTo
                    patients {
                        id
                        name
                        surname
                        therapyUnits {
                            id
                            activity {
                                id
                                code
                                name
                            }
                            duration
                            weeklyNumber
                        }
                    }
                }
            }`,
            variables: {periodId: periodId}
        });
        return promise.data.period;
    }

    async eventsForOffice(officeId: number) {
        const promise = await apollo.query({
            query: gql`query ($officeId: Int!){
                eventsForOffice(officeId: $officeId) {
                    Id
                    Subject
                    Description
                    Name
                    StartTime
                    EndTime
                    ProjectId
                    TaskId
                    IsBlock
                    PrimaryColor
                    SecondaryColor
                    Constraint
                }
            }`,
            variables: {officeId: officeId}
        });
        return promise.data.eventsForOffice;
    }

    async sendNotifications(date: String) {
        return await apollo.mutate({
            mutation: gql`mutation($date: LocalDate!) {
                sendNotifications(date: $date)
            }`,
            variables: {
                date: date
            }
        });
    }

    async sendNotificationsToTherapists(date: String) {
        return await apollo.mutate({
            mutation: gql`mutation($date: LocalDate!) {
                sendNotificationsToTherapists(date: $date)
            }`,
            variables: {
                date: date
            }
        });
    }

}

export default new SchedulerService();
